import React, { useState, useEffect } from 'react';
import Court from './Court';
import Sidebar from './Sidebar';
import { calculateDINKRRating } from '../utils/ratingCalculation';
import { generateDisplayNames } from '../utils/nameUtils';

const GameInterface = ({ players, sessionConfig, rotationManager, onSessionComplete }) => {
  // Helper function to display competition level names
  const getCompetitionLevelDisplay = (level) => {
    const levels = {
      'recreational': 'Recreational',
      'competitive': 'Competitive', 
      'tournament': 'Tournament',
      'professional': 'Professional',
      'no-scoring': 'Do Not Track Scores'
    };
    return levels[level] || 'League';
  };

  // Extract dynamic configuration with validation
  const { courtCount = 3, sessionType } = sessionConfig || {};
  const maxPlayers = courtCount * 8;
  const isMLPMode = sessionType === 'mlp';

  // Common state
  const [allPlayers, setAllPlayers] = useState([]);
  const [courts, setCourts] = useState([]);
  const [isSessionStarted, setIsSessionStarted] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // SMART mode state
  const [nextPlayersInfo, setNextPlayersInfo] = useState({ players: [], partnerships: null, formatted: '' });

  // MLP mode state
  const [mlpGames, setMLPGames] = useState([]);
  const [teamRosters, setTeamRosters] = useState({});
  const [waitingPlayers, setWaitingPlayers] = useState([]);

  // Generate the exact 24 MLP games
  const generateMLPGames = () => {
    const games = [];
    let gameId = 1;

    // Men's Doubles (6 games)
    const menPairs = [
      ['A', 'B'], ['A', 'C'], ['A', 'D'], ['B', 'C'], ['B', 'D'], ['C', 'D']
    ];
    
    menPairs.forEach(([team1, team2]) => {
      games.push({
        id: gameId++,
        team1,
        team2,
        gameType: 'MM',
        gameTypeLabel: "Men's Doubles",
        status: 'pending',
        result: null,
        courtAssigned: null,
        players: {
          team1: [`${team1}-M1`, `${team1}-M2`],
          team2: [`${team2}-M1`, `${team2}-M2`]
        }
      });
    });

    // Women's Doubles (6 games)
    const womenPairs = [
      ['A', 'B'], ['A', 'C'], ['A', 'D'], ['B', 'C'], ['B', 'D'], ['C', 'D']
    ];
    
    womenPairs.forEach(([team1, team2]) => {
      games.push({
        id: gameId++,
        team1,
        team2,
        gameType: 'FF',
        gameTypeLabel: "Women's Doubles",
        status: 'pending',
        result: null,
        courtAssigned: null,
        players: {
          team1: [`${team1}-F1`, `${team1}-F2`],
          team2: [`${team2}-F1`, `${team2}-F2`]
        }
      });
    });

    // Mixed Doubles 1 (M1+F1) (6 games)
    const mixed1Pairs = [
      ['A', 'B'], ['A', 'C'], ['A', 'D'], ['B', 'C'], ['B', 'D'], ['C', 'D']
    ];
    
    mixed1Pairs.forEach(([team1, team2]) => {
      games.push({
        id: gameId++,
        team1,
        team2,
        gameType: 'MX1',
        gameTypeLabel: 'Mixed Doubles (M1+F1)',
        status: 'pending',
        result: null,
        courtAssigned: null,
        players: {
          team1: [`${team1}-M1`, `${team1}-F1`],
          team2: [`${team2}-M1`, `${team2}-F1`]
        }
      });
    });

    // Mixed Doubles 2 (M2+F2) (6 games)
    const mixed2Pairs = [
      ['A', 'B'], ['A', 'C'], ['A', 'D'], ['B', 'C'], ['B', 'D'], ['C', 'D']
    ];
    
    mixed2Pairs.forEach(([team1, team2]) => {
      games.push({
        id: gameId++,
        team1,
        team2,
        gameType: 'MX2',
        gameTypeLabel: 'Mixed Doubles (M2+F2)',
        status: 'pending',
        result: null,
        courtAssigned: null,
        players: {
          team1: [`${team1}-M2`, `${team1}-F2`],
          team2: [`${team2}-M2`, `${team2}-F2`]
        }
      });
    });

    return games;
  };

  // Assign 16 players to 4 teams
  const assignPlayersToTeams = (players) => {
    if (players.length !== 16) {
      throw new Error('MLP tournament requires exactly 16 players');
    }

    const males = players.filter(p => p.gender === 'Male');
    const females = players.filter(p => p.gender === 'Female');

    if (males.length !== 8 || females.length !== 8) {
      throw new Error('MLP tournament requires exactly 8 male and 8 female players');
    }

    males.sort((a, b) => (b.rating || 3.5) - (a.rating || 3.5));
    females.sort((a, b) => (b.rating || 3.5) - (a.rating || 3.5));

    const teams = {
      A: { name: 'Lightning', players: {} },
      B: { name: 'Thunder', players: {} },
      C: { name: 'Storm', players: {} },
      D: { name: 'Cyclone', players: {} }
    };

    // Assign males using snake draft (A,B,C,D,D,C,B,A)
    const maleOrder = ['A', 'B', 'C', 'D', 'D', 'C', 'B', 'A'];
    males.forEach((player, index) => {
      const teamLetter = maleOrder[index];
      const position = teams[teamLetter].players['M1'] ? 'M2' : 'M1';
      teams[teamLetter].players[position] = {
        ...player,
        team: teamLetter,
        position,
        displayName: `${teamLetter}-${position}`
      };
    });

    // Assign females using snake draft
    const femaleOrder = ['A', 'B', 'C', 'D', 'D', 'C', 'B', 'A'];
    females.forEach((player, index) => {
      const teamLetter = femaleOrder[index];
      const position = teams[teamLetter].players['F1'] ? 'F2' : 'F1';
      teams[teamLetter].players[position] = {
        ...player,
        team: teamLetter,
        position,
        displayName: `${teamLetter}-${position}`
      };
    });

    return teams;
  };

  // Get actual player objects from game player codes
  const getPlayersFromGame = (game, teamRosters) => {
    const allPlayerCodes = [...game.players.team1, ...game.players.team2];
    const actualPlayers = [];
    
    allPlayerCodes.forEach(code => {
      const [team, position] = code.split('-');
      if (teamRosters[team] && teamRosters[team].players[position]) {
        actualPlayers.push(teamRosters[team].players[position]);
      }
    });
    
    return actualPlayers;
  };

  // Enhanced debugging version of findNextMLPGame
  const findNextMLPGame = (availablePlayerIds, mlpGames, teamRosters) => {
    console.log('=== FINDING NEXT MLP GAME ===');
    console.log('Available player IDs:', availablePlayerIds);
    console.log('Total available players:', availablePlayerIds.length);
    
    // Log team rosters
    Object.entries(teamRosters).forEach(([team, roster]) => {
      console.log(`Team ${team} (${roster.name}):`);
      Object.entries(roster.players).forEach(([position, player]) => {
        const isAvailable = availablePlayerIds.includes(player.id);
        console.log(`  ${position}: ${player.name} (${player.id}) - ${isAvailable ? 'AVAILABLE' : 'NOT AVAILABLE'}`);
      });
    });
    
    const pendingGames = mlpGames.filter(game => game.status === 'pending');
    console.log(`Checking ${pendingGames.length} pending games out of ${mlpGames.length} total games`);
    
    for (let i = 0; i < pendingGames.length; i++) {
      const game = pendingGames[i];
      console.log(`\nChecking Game ${game.id}: ${game.team1} vs ${game.team2} (${game.gameType})`);
      console.log(`Required players: ${game.players.team1.join(', ')} vs ${game.players.team2.join(', ')}`);
      
      const requiredPlayers = getPlayersFromGame(game, teamRosters);
      console.log('Actual required players:', requiredPlayers.map(p => `${p.name} (${p.id})`).join(', '));
      
      const availabilityCheck = requiredPlayers.map(player => {
        const isAvailable = availablePlayerIds.includes(player.id);
        console.log(`  ${player.name} (${player.displayName}): ${isAvailable ? 'AVAILABLE' : 'NOT AVAILABLE'}`);
        return isAvailable;
      });
      
      const canPlay = availabilityCheck.every(available => available);
      console.log(`Can play this game: ${canPlay ? 'YES' : 'NO'}`);
      
      if (canPlay) {
        console.log(`✓ FOUND PLAYABLE GAME: ${game.id}`);
        return { game, players: requiredPlayers };
      }
    }
    
    console.log('✗ NO PLAYABLE GAMES FOUND');
    return null;
  };

  // Get team name from team letter
  const getTeamName = (teamLetter) => {
    const teamNames = {
      A: 'Lightning',
      B: 'Thunder', 
      C: 'Storm',
      D: 'Cyclone'
    };
    return teamNames[teamLetter] || `Team ${teamLetter}`;
  };

  // Calculate tournament standings
  const calculateTournamentStandings = (mlpGames, teamRosters) => {
    const standings = ['A', 'B', 'C', 'D'].map(team => ({
      team,
      teamName: getTeamName(team),
      wins: 0,
      losses: 0,
      winPercentage: 0
    }));

    const completedGames = mlpGames.filter(game => game.status === 'completed');
    
    completedGames.forEach(game => {
      if (game.result) {
        const winner = game.result.winner;
        const loser = winner === game.team1 ? game.team2 : game.team1;
        
        const winnerStanding = standings.find(s => s.team === winner);
        const loserStanding = standings.find(s => s.team === loser);
        
        if (winnerStanding) winnerStanding.wins++;
        if (loserStanding) loserStanding.losses++;
      }
    });

    standings.forEach(team => {
      const totalGames = team.wins + team.losses;
      team.winPercentage = totalGames > 0 ? Math.round((team.wins / totalGames) * 100) : 0;
    });

    standings.sort((a, b) => b.winPercentage - a.winPercentage || b.wins - a.wins);

    return standings;
  };

  // Initialize everything in useEffect
  useEffect(() => {
    if (players && Array.isArray(players) && players.length > 0 && !isInitialized) {
      console.log('Initializing GameInterface with players:', players.length);
      
      const playersArray = players.filter(p => p && p.id);
      const processedPlayers = playersArray.map(p => ({ 
        ...p, 
        currentRating: p.rating || 3.5,
        gamesPlayed: 0, 
        partnerships: new Set(), 
        waitTime: 0, 
        sessionChange: 0,
        lastGameEnd: 0,
        lineJumps: 0,
        gamesUntilEligible: 0,
        waitHistory: []
      }));

      const playersWithDisplayNames = isMLPMode ? processedPlayers : generateDisplayNames(processedPlayers);
      
      setAllPlayers(playersWithDisplayNames);
      
      if (isMLPMode) {
        // Initialize empty courts for MLP - games will be assigned when session starts
        const emptyCourts = Array.from({ length: courtCount }, (_, i) => ({
          id: i + 1,
          players: [],
          gameInfo: null,
          timer: { startTime: null, isActive: false, duration: 12 * 60 },
          isActive: false
        }));
        setCourts(emptyCourts);
      } else {
        // Initialize courts with players for SMART mode
        const initialCourts = [];
        for (let i = 0; i < courtCount; i++) {
          const startIndex = i * 4;
          const courtPlayers = playersWithDisplayNames.slice(startIndex, startIndex + 4);
          
          initialCourts.push({
            id: i + 1,
            players: courtPlayers,
            timer: { startTime: null, isActive: false, duration: 12 * 60 },
            isActive: false
          });
        }
        setCourts(initialCourts);
      }
      
      setIsInitialized(true);
    }
  }, [players, courtCount, isInitialized, isMLPMode]);

  // SMART mode bench calculation
  const getBench = () => {
    if (isMLPMode || !Array.isArray(allPlayers) || !Array.isArray(courts) || allPlayers.length === 0 || courts.length === 0) {
      return [];
    }

    try {
      const onCourtIds = new Set();
      
      for (const court of courts) {
        if (court && Array.isArray(court.players)) {
          for (const player of court.players) {
            if (player && player.id) {
              onCourtIds.add(player.id);
            }
          }
        }
      }

      return allPlayers.filter(p => p && p.id && !onCourtIds.has(p.id));
    } catch (error) {
      console.warn('Error calculating bench:', error);
      return [];
    }
  };

  const bench = getBench();

  // SMART mode next players calculation
  useEffect(() => {
    if (!isInitialized || isMLPMode) return;
    
    const eligiblePlayers = bench.filter(p => (p.gamesUntilEligible || 0) <= 0);
    
    if (eligiblePlayers.length >= 4) {
      const selectedPlayers = eligiblePlayers.slice(0, 4);
      
      const result = rotationManager && rotationManager.assignOptimalPartnerships ? 
        rotationManager.assignOptimalPartnerships(selectedPlayers) : null;
      
      if (result && result.partnerships) {
        setNextPlayersInfo({
          players: selectedPlayers,
          partnerships: result.partnerships,
          formatted: result.formatted
        });
      } else {
        setNextPlayersInfo({
          players: selectedPlayers,
          partnerships: null,
          formatted: selectedPlayers.map(p => p.name).join(', ')
        });
      }
    } else {
      setNextPlayersInfo({ players: [], partnerships: null, formatted: 'Not enough eligible players' });
    }
  }, [bench, isInitialized, rotationManager, isMLPMode]);

  // Initialize MLP Tournament
  const initializeMLPTournament = () => {
    console.log('Initializing MLP Tournament with 16 players');
    
    try {
      const rosters = assignPlayersToTeams(allPlayers);
      setTeamRosters(rosters);
      
      const games = generateMLPGames();
      setMLPGames(games);
      
      // Start first 3 games
      const initialGames = [];
      let availablePlayerIds = allPlayers.map(p => p.id);
      
      for (let courtIndex = 0; courtIndex < 3; courtIndex++) {
        const nextMatch = findNextMLPGame(availablePlayerIds, games, rosters);
        if (nextMatch) {
          initialGames.push(nextMatch);
          nextMatch.players.forEach(p => {
            const index = availablePlayerIds.indexOf(p.id);
            if (index > -1) availablePlayerIds.splice(index, 1);
          });
        }
      }
      
      // Set up courts
      const initialCourts = [];
      for (let i = 0; i < 3; i++) {
        if (initialGames[i]) {
          initialCourts.push({
            id: i + 1,
            players: initialGames[i].players.map(p => p.displayName),
            gameInfo: {
              ...initialGames[i].game,
              status: 'in-progress',
              courtAssigned: i + 1
            },
            timer: { startTime: Date.now(), isActive: true, duration: 12 * 60 },
            isActive: true
          });
        } else {
          initialCourts.push({
            id: i + 1,
            players: [],
            gameInfo: null,
            timer: { startTime: null, isActive: false, duration: 12 * 60 },
            isActive: false
          });
        }
      }
      
      setCourts(initialCourts);
      
      // Set waiting players
      const playingPlayerIds = initialGames.flatMap(game => game.players.map(p => p.id));
      const waitingPlayerObjects = Object.values(rosters)
        .flatMap(team => Object.values(team.players))
        .filter(player => !playingPlayerIds.includes(player.id));
      
      setWaitingPlayers(waitingPlayerObjects);
      
      // Update games status
      setMLPGames(prev => prev.map(game => {
        const matchingGame = initialGames.find(ig => ig.game.id === game.id);
        if (matchingGame) {
          return { ...game, status: 'in-progress', courtAssigned: matchingGame.game.courtAssigned };
        }
        return game;
      }));
      
      console.log(`MLP Tournament initialized: ${games.length} total games, ${initialGames.length} games started`);
    } catch (error) {
      console.error('Failed to initialize MLP tournament:', error);
    }
  };

  // Start session
  const startSession = () => {
    const now = Date.now();
    setIsSessionStarted(true);
    
    if (isMLPMode) {
      initializeMLPTournament();
    } else {
      setCourts(prev => prev.map(court => ({
        ...court,
        timer: { startTime: now, isActive: true, duration: 12 * 60 },
        isActive: true
      })));

      if (rotationManager && rotationManager.initializeSession) {
        const initialCourtPlayers = courts.map(court => court.players || []);
        const initialNextUp = nextPlayersInfo.players || [];
        
        console.log('Initializing SMART system with initial bench tracking');
        rotationManager.initializeSession(allPlayers, initialCourtPlayers, initialNextUp);
      }
    }
  };

  // Enhanced debugging MLP Score submission handler
  const handleMLPScoreSubmit = (courtId, team1Score, team2Score) => {
    console.log(`\n=== MLP GAME COMPLETE: Court ${courtId} finished ${team1Score}-${team2Score} ===`);
    
    const courtIndex = courts.findIndex(c => c && c.id === courtId);
    const court = courts[courtIndex];
    
    if (!court || !court.gameInfo) {
      console.error('No game info found for court');
      return;
    }
    
    console.log('Completed game info:', court.gameInfo);
    
    const winner = team1Score > team2Score ? court.gameInfo.team1 : court.gameInfo.team2;
    console.log(`Winner: Team ${winner}`);
    
    // Update the completed game
    setMLPGames(prev => {
      const updated = prev.map(game => 
        game.id === court.gameInfo.id ? {
          ...game,
          status: 'completed',
          result: { team1Score, team2Score, winner },
          courtAssigned: null
        } : game
      );
      
      const completedCount = updated.filter(g => g.status === 'completed').length;
      console.log(`Games completed: ${completedCount}/24`);
      
      return updated;
    });
    
    // Get available players with detailed logging
    console.log('\n--- CALCULATING AVAILABLE PLAYERS ---');
    
    const finishedPlayers = getPlayersFromGame(court.gameInfo, teamRosters);
    console.log('Players who just finished:', finishedPlayers.map(p => `${p.name} (${p.id})`));
    
    const finishedPlayerIds = finishedPlayers.map(p => p.id);
    console.log('Finished player IDs:', finishedPlayerIds);
    
    const benchPlayerIds = waitingPlayers.map(p => p.id);
    console.log('Bench player IDs:', benchPlayerIds);
    console.log('Bench players:', waitingPlayers.map(p => `${p.name} (${p.id})`));
    
    const availablePlayerIds = [...finishedPlayerIds, ...benchPlayerIds];
    console.log('Total available player IDs:', availablePlayerIds);
    console.log(`Available players count: ${availablePlayerIds.length} (should be 8)`);
    
    if (availablePlayerIds.length !== 8) {
      console.warn(`⚠️ WARNING: Expected 8 available players but got ${availablePlayerIds.length}`);
    }
    
    // Find next available game with enhanced debugging
    const nextMatch = findNextMLPGame(availablePlayerIds, mlpGames, teamRosters);
    
    if (nextMatch) {
      console.log(`\n✓ Next game found: ${getTeamName(nextMatch.game.team1)} vs ${getTeamName(nextMatch.game.team2)} - ${nextMatch.game.gameTypeLabel}`);
      console.log('Players for next game:', nextMatch.players.map(p => `${p.name} (${p.displayName})`));
      
      // Update court with new game
      setCourts(prev => {
        const newCourts = [...prev];
        newCourts[courtIndex] = {
          ...court,
          players: nextMatch.players.map(p => p.displayName),
          gameInfo: {
            ...nextMatch.game,
            status: 'in-progress',
            courtAssigned: courtId
          },
          timer: { startTime: Date.now(), isActive: true, duration: 12 * 60 },
          isActive: true
        };
        console.log(`Updated Court ${courtId} with new game`);
        return newCourts;
      });
      
      // Update game status
      setMLPGames(prev => prev.map(game => 
        game.id === nextMatch.game.id ? {
          ...game,
          status: 'in-progress',
          courtAssigned: courtId
        } : game
      ));
      
      // Update waiting players
      const newWaitingPlayerIds = availablePlayerIds.filter(id => 
        !nextMatch.players.some(p => p.id === id)
      );
      
      console.log('New waiting player IDs:', newWaitingPlayerIds);
      
      const newWaitingPlayers = newWaitingPlayerIds.map(id => 
        Object.values(teamRosters).flatMap(team => Object.values(team.players))
          .find(p => p.id === id)
      ).filter(Boolean);
      
      console.log('New waiting players:', newWaitingPlayers.map(p => `${p.name} (${p.displayName})`));
      
      setWaitingPlayers(newWaitingPlayers);
      
    } else {
      console.log('\n✗ No games available with current 8 players');
      console.log('This should rarely happen in a proper MLP tournament');
      
      // Debug: Show what games are still pending and why they can't be played
      const pendingGames = mlpGames.filter(g => g.status === 'pending');
      console.log('\nPending games that could not be matched:');
      pendingGames.slice(0, 5).forEach(game => {
        console.log(`Game ${game.id}: ${game.team1} vs ${game.team2} (${game.gameType})`);
        const requiredPlayers = getPlayersFromGame(game, teamRosters);
        requiredPlayers.forEach(player => {
          const available = availablePlayerIds.includes(player.id);
          console.log(`  Need: ${player.name} (${player.displayName}) - ${available ? 'Available' : 'Not Available'}`);
        });
      });
      
      // Court becomes idle
      setCourts(prev => {
        const newCourts = [...prev];
        newCourts[courtIndex] = {
          ...court,
          players: [],
          gameInfo: null,
          timer: { startTime: null, isActive: false, duration: 12 * 60 },
          isActive: false
        };
        console.log(`Court ${courtId} set to idle`);
        return newCourts;
      });
      
      // All available players go to bench
      const allAvailablePlayers = availablePlayerIds.map(id => 
        Object.values(teamRosters).flatMap(team => Object.values(team.players))
          .find(p => p.id === id)
      ).filter(Boolean);
      
      setWaitingPlayers(allAvailablePlayers);
      console.log('All available players moved to bench:', allAvailablePlayers.map(p => p.name));
    }
    
    console.log('=== MLP SCORE SUBMIT COMPLETE ===\n');
  };

  // SMART mode functions (existing logic)
  const assignWaitTimes = (exitingPlayers) => {
    if (!Array.isArray(exitingPlayers)) {
      return [];
    }
    
    const currentBenchSize = bench.length;
    let waitDistribution = [1, 1, 2, 2];
    
    if (currentBenchSize <= 6) {
      waitDistribution = [1, 1, 2, 2];
    } else if (currentBenchSize <= 9) {
      waitDistribution = [1, 2, 3, 3];
    } else if (currentBenchSize <= 12) {
      waitDistribution = [1, 2, 3, 4];
    } else {
      waitDistribution = [1, 2, 4, 5];
    }
    
    console.log(`Adaptive wait distribution for bench size ${currentBenchSize}: [${waitDistribution.join(',')}]`);
    
    return exitingPlayers.map((player, index) => ({
      ...player,
      gamesUntilEligible: waitDistribution[index] || 1,
      waitHistory: [...(player.waitHistory || []), waitDistribution[index] || 1]
    }));
  };

  // SMART mode score submission
  const handleSMARTScoreSubmit = (courtId, team1Score, team2Score) => {
    if (!sessionConfig || !courts.length) return;
    
    const isNonScoringMode = sessionConfig.competitionLevel === 'no-scoring';
    const logMessage = isNonScoringMode ? 
      `=== DYNAMIC SMART ROTATION: Court ${courtId} finished (no scoring) ===` :
      `=== DYNAMIC SMART ROTATION: Court ${courtId} finished ${team1Score}-${team2Score} ===`;
    
    console.log(logMessage);
    
    const courtIndex = courts.findIndex(c => c && c.id === courtId);
    const court = courts[courtIndex];
    
    if (!court || !Array.isArray(court.players)) return;
    
    const team1 = court.players.slice(0, 2).filter(Boolean);
    const team2 = court.players.slice(2, 4).filter(Boolean);
    const exitingPlayers = [...team1, ...team2];
    
    const committedNextUp = Array.isArray(nextPlayersInfo.players) ? [...nextPlayersInfo.players] : [];
    
    let updatedExitingPlayers;
    
    if (isNonScoringMode) {
      updatedExitingPlayers = exitingPlayers.map(player => ({
        ...player,
        gamesPlayed: (player.gamesPlayed || 0) + 1,
        lastGameEnd: (rotationManager && rotationManager.gameNumber) || 1
      }));
    } else {
      const winningTeam = team1Score > team2Score ? 1 : 2;
      const team1Ratings = team1.map(p => p.currentRating || p.rating || 3.5);
      const team2Ratings = team2.map(p => p.currentRating || p.rating || 3.5);
      const actualScores = { team1: team1Score, team2: team2Score };
      
      const dinkrResult = calculateDINKRRating(
        team1Ratings, 
        team2Ratings, 
        actualScores, 
        winningTeam, 
        sessionConfig.competitionLevel,
        [...team1, ...team2]
      );
      
      updatedExitingPlayers = exitingPlayers.map((player, index) => ({
        ...player,
        currentRating: dinkrResult.newRatings[index],
        sessionChange: (player.sessionChange || 0) + (dinkrResult.newRatings[index] - (player.currentRating || player.rating || 3.5)),
        gamesPlayed: (player.gamesPlayed || 0) + 1,
        lastGameEnd: (rotationManager && rotationManager.gameNumber) || 1
      }));
    }
    
    // Update partnerships
    if (updatedExitingPlayers.length >= 4) {
      updatedExitingPlayers[0].partnerships.add(updatedExitingPlayers[1].id);
      updatedExitingPlayers[1].partnerships.add(updatedExitingPlayers[0].id);
      updatedExitingPlayers[2].partnerships.add(updatedExitingPlayers[3].id);
      updatedExitingPlayers[3].partnerships.add(updatedExitingPlayers[2].id);
    }
    
    // Apply adaptive wait times
    updatedExitingPlayers = assignWaitTimes(updatedExitingPlayers);
    
    // Advance game counter
    if (rotationManager) {
      if (typeof rotationManager.nextGame === 'function') {
        rotationManager.nextGame();
      } else {
        rotationManager.gameNumber = (rotationManager.gameNumber || 1) + 1;
      }
    }
    
    // Update all players
    setAllPlayers(prev => prev.map(player => {
      const exitingUpdate = updatedExitingPlayers.find(up => up && up.id === (player && player.id));
      if (exitingUpdate) {
        return exitingUpdate;
      }
      
      if ((player.gamesUntilEligible || 0) > 0) {
        return {
          ...player,
          gamesUntilEligible: Math.max(0, (player.gamesUntilEligible || 0) - 1)
        };
      }
      
      return player;
    }));
    
    // Court rotation
    setCourts(prev => {
      const newCourts = [...prev];
      const finishedCourtIndex = courtIndex;
      
      // Shift courts
      for (let i = finishedCourtIndex; i > 0; i--) {
        if (newCourts[i - 1] && newCourts[i - 1].isActive) {
          newCourts[i] = {
            ...newCourts[i - 1],
            id: newCourts[i].id
          };
        }
      }
      
      // Put Next Up on top court
      newCourts[0] = {
        id: 1,
        players: committedNextUp,
        timer: { startTime: Date.now(), isActive: true, duration: 12 * 60 },
        isActive: true
      };
      
      return newCourts;
    });
  };

  // Combined score submission handler
  const handleScoreSubmit = (courtId, team1Score, team2Score) => {
    if (isMLPMode) {
      handleMLPScoreSubmit(courtId, team1Score, team2Score);
    } else {
      handleSMARTScoreSubmit(courtId, team1Score, team2Score);
    }
  };

  // Don't render until initialized
  if (!isInitialized) {
    return <div>Loading...</div>;
  }
  
  return (
    <div className="game-interface">
      <div className="game-content">
        <div className="courts-section">
          {!isSessionStarted ? (
            <div className="session-start">
              <h2>Ready to Start {isMLPMode ? 'MLP Tournament' : 'SMART Session'}</h2>
              <p>{allPlayers.length} players ready to play</p>
              <p style={{ opacity: 0.8, marginBottom: '20px' }}>
                {isMLPMode ? (
                  <>Tournament Format: 4 Teams • 24 Total Games • Round Robin</>
                ) : (
                  <>
                    Competition Level: <strong>{getCompetitionLevelDisplay(sessionConfig && sessionConfig.competitionLevel)}</strong> | 
                    {(sessionConfig && sessionConfig.competitionLevel) === 'no-scoring' ? 'No Rating Tracking' : 'DiNKR V2.0'} | 
                    Dynamic SMART Distribution |
                    {allPlayers.length} Players on {courtCount} Courts (Max: {maxPlayers})
                  </>
                )}
              </p>
              <button onClick={startSession} className="start-session-button">
                {isMLPMode ? 'Start Tournament' : 'Start All Courts'}
              </button>
            </div>
          ) : (
            <div className="courts-container">
              {courts.length > 0 && courts.slice().reverse().map((court) => (
                court && <Court
                  key={court.id}
                  courtData={court}
                  onScoreSubmit={handleScoreSubmit}
                  allPlayers={allPlayers}
                  competitionLevel={sessionConfig && sessionConfig.competitionLevel}
                  gameInfo={court.gameInfo}
                  getTeamName={getTeamName}
                />
              ))}
            </div>
          )}
        </div>
        
        <Sidebar 
          mode={isMLPMode ? 'mlp' : 'smart'}
          tournamentData={isMLPMode ? {
            progress: {
              completedGames: mlpGames.filter(g => g.status === 'completed').length,
              progressPercentage: Math.round((mlpGames.filter(g => g.status === 'completed').length / 24) * 100)
            },
            standings: calculateTournamentStandings(mlpGames, teamRosters),
            remainingGames: {
              total: 24 - mlpGames.filter(g => g.status === 'completed').length,
              completed: mlpGames.filter(g => g.status === 'completed').length,
              breakdown: {
                MM: { count: mlpGames.filter(g => g.gameType === 'MM' && g.status === 'pending').length },
                FF: { count: mlpGames.filter(g => g.gameType === 'FF' && g.status === 'pending').length },
                MX1: { count: mlpGames.filter(g => g.gameType === 'MX1' && g.status === 'pending').length },
                MX2: { count: mlpGames.filter(g => g.gameType === 'MX2' && g.status === 'pending').length }
              }
            },
            waitingPlayers: waitingPlayers.map(p => p.displayName)
          } : null}
          getTeamName={getTeamName}
          allPlayers={allPlayers}
          bench={bench}
          nextPlayersInfo={nextPlayersInfo}
          rotationManager={rotationManager}
          competitionLevel={(sessionConfig && sessionConfig.competitionLevel) || 'league'}
          maxPlayers={maxPlayers}
        />
      </div>
    </div>
  );
};

export default GameInterface;